import React, { useState, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  getNavItemsForRole,
  getDefaultRouteForRole,
  GATED_SETUP_ITEM_IDS,
} from "../../constants/navigation";
import { LogoMark } from "../ui/Logo";
import { LanguageMenu, LanguageToggle } from "../ui/LanguageSwitcher";
import { useDismiss } from "../../hooks/useDismiss";

/**
 * Dashboard Menu Component - Navigation for logged-in users
 *
 * Which tabs appear comes from the caller's role. The same list guards the
 * routes in App.jsx, so hiding a tab also closes the URL behind it.
 *
 * `isSetupComplete = false` keeps the 8 setup-gated items (see
 * `GATED_SETUP_ITEM_IDS`) in their normal slot but renders them as
 * non-navigating — never hidden, per the "never hide a tab" rule: a hidden
 * tab is a URL that still quietly works for anyone who types it.
 */
const DashboardMenu = ({
  user,
  onLogout,
  onExitImpersonation,
  isSetupComplete = true,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const closeAccount = useCallback(() => setAccountOpen(false), []);
  useDismiss(accountRef, accountOpen, closeAccount);

  const email = user?.email || t("Dashboard.Menu.defaultUser");

  const impersonating = Boolean(user?.impersonating);
  const menuItems = getNavItemsForRole(user?.role, { impersonating });
  // The logo goes to the same page login lands on — Overview for a customer,
  // the role's first tab for everyone else — so it can never point at a tab
  // the role does not have.
  const homePath = getDefaultRouteForRole(user?.role, { impersonating });

  const isActive = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      {/* Mobile Menu Toggle. Hidden while the drawer is open — the drawer
          carries its own close button, so a fixed one here would land on top
          of the logo. */}
      {!mobileMenuOpen && (
        <button
          className="fixed top-5 left-5 z-1001 flex size-11 items-center justify-center rounded-lg bg-white shadow-card transition hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 lg:hidden"
          onClick={toggleMobileMenu}
          aria-label={t("Dashboard.Menu.openMenu")}
          aria-expanded={false}
          aria-controls="dashboard-sidebar"
        >
          <i className="bi bi-list text-2xl text-ink-900" />
        </button>
      )}

      {/* Language on a phone: in the strip the page leaves for the menu
          toggle, so it is one tap rather than drawer → account → language. */}
      {!mobileMenuOpen && (
        <div className="fixed top-5 right-5 z-1001 lg:hidden">
          <LanguageMenu triggerClassName="h-11 rounded-lg bg-white px-3 text-sm font-semibold text-ink-700 shadow-card hover:bg-ink-50 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600" />
        </div>
      )}

      {/* Sidebar */}
      <aside
        id="dashboard-sidebar"
        className={`fixed top-0 left-0 z-1000 flex h-screen w-full max-w-70 flex-col border-r border-ink-200 bg-white transition-transform duration-300 motion-reduce:transition-none lg:w-70 lg:translate-x-0 ${
          mobileMenuOpen ? "translate-x-0 shadow-card-hover" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between gap-3 border-b border-ink-200 px-5 py-7">
          <Link
            to={homePath}
            className="flex items-center gap-3 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
            onClick={() => setMobileMenuOpen(false)}
          >
            <LogoMark className="size-9 shrink-0 text-brand-600" />
            <h1 className="m-0 text-xl font-bold tracking-tight text-ink-900 sm:text-2xl">
              Easy<span className="text-brand-600">Planner</span>
            </h1>
          </Link>
          <button
            className="-mr-2 flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-ink-500 transition hover:bg-ink-50 hover:text-ink-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-label={t("Dashboard.Menu.closeMenu")}
          >
            <i className="bi bi-x text-2xl" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-5">
          <ul className="m-0 list-none p-0">
            {menuItems.map((item) => {
              const isGated = !isSetupComplete && GATED_SETUP_ITEM_IDS.has(item.id);

              if (isGated) {
                return (
                  <li key={item.id} className="mb-1">
                    <span
                      aria-disabled="true"
                      title={t("Dashboard.Menu.setupRequiredTooltip")}
                      className="flex cursor-not-allowed items-center gap-3 border-l-3 border-transparent px-4 py-3 text-sm font-medium text-ink-300 select-none sm:px-5 sm:py-3.5 sm:text-[0.9375rem]"
                    >
                      <i
                        className={`bi ${item.icon} w-6 text-center text-xl`}
                        aria-hidden="true"
                      />
                      <span className="flex-1">{t(item.labelKey)}</span>
                      <i
                        className="bi bi-lock-fill text-xs text-ink-300"
                        aria-hidden="true"
                      />
                    </span>
                  </li>
                );
              }

              return (
                <li key={item.id} className="mb-1">
                  <Link
                    to={item.path}
                    aria-current={isActive(item.path) ? "page" : undefined}
                    className={`flex items-center gap-3 border-l-3 px-4 py-3 text-sm font-medium transition sm:px-5 sm:py-3.5 sm:text-[0.9375rem] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-600 ${
                      isActive(item.path)
                        ? "border-brand-600 bg-brand-50 text-brand-600"
                        : "border-transparent text-ink-500 hover:bg-ink-50 hover:text-brand-600"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className={`bi ${item.icon} w-6 text-center text-xl`} />
                    <span>{t(item.labelKey)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="border-t border-ink-200 p-3">
          {/* The account menu: who is signed in, the interface language and the
              way out, behind one row instead of three stacked buttons. */}
          <div ref={accountRef} className="relative">
            {accountOpen && (
              <div
                id="account-menu"
                className="absolute inset-x-0 bottom-full mb-2 overflow-hidden rounded-xl border border-ink-200 bg-white shadow-card-hover"
              >
                <div className="px-4 py-3">
                  <LanguageToggle />
                </div>
                {/* While simulating, signing out would also drop the platform
                    session set aside behind it — almost never what the person
                    clicking meant. Exit simulation below replaces it. */}
                {!impersonating && (
                  <div className="border-t border-ink-100 p-1">
                    <button
                      type="button"
                      className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-ink-600 transition hover:bg-red-50 hover:text-red-600 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-600"
                      onClick={onLogout}
                    >
                      <i className="bi bi-box-arrow-right text-base" aria-hidden="true" />
                      <span>{t("Menu.Logout")}</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => setAccountOpen((v) => !v)}
              aria-expanded={accountOpen}
              aria-haspopup="true"
              aria-controls="account-menu"
              className={`flex w-full cursor-pointer items-center gap-3 rounded-lg p-2 text-left transition hover:bg-ink-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-600 ${
                accountOpen ? "bg-ink-50" : ""
              }`}
            >
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-semibold text-white uppercase"
                aria-hidden="true"
              >
                {email.charAt(0)}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-900">
                {email}
              </span>
              <i className="bi bi-chevron-expand text-ink-400" aria-hidden="true" />
            </button>
          </div>

          {/* Kept out of the account menu: leaving a simulation is the one
              action that must never be a click away from being found. */}
          {impersonating && onExitImpersonation && (
            <button
              type="button"
              className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800 transition hover:border-amber-400 hover:bg-amber-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
              onClick={onExitImpersonation}
            >
              <i className="bi bi-box-arrow-left text-lg" aria-hidden="true" />
              <span>{t("Dashboard.Impersonation.exit")}</span>
            </button>
          )}
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-999 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

DashboardMenu.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string,
    role: PropTypes.number,
    impersonating: PropTypes.bool,
  }),
  onLogout: PropTypes.func.isRequired,
  onExitImpersonation: PropTypes.func,
  isSetupComplete: PropTypes.bool,
};

export default DashboardMenu;
